import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type CopyReport, type Database, useDuplicateDatabase } from '@/entities/database';
import { useWorkspaces } from '@/entities/workspace';
import { FormError } from '@/shared/forms';
import { Button, Combobox, Dialog, Icons, TextField } from '@/shared/ui';

import styles from './DatabaseCatalog.module.css';
import { DATABASE_NAME_MAX } from './CreateDatabaseDialog';
import { CREATE_DATABASE } from './permissions';

interface Props {
  source: Database;
  onDuplicated: (database: Database, report: CopyReport) => void;
  onClose: () => void;
}

/**
 * "Duplicate database": a name, the destination workspace (only those where the viewer may
 * create databases), structure only or with records. The copy is a new database owned by the
 * viewer; nobody else gets access to it, and confidentiality is never lowered. Copying
 * confidential content to ANOTHER workspace asks for an explicit confirmation.
 */
export function DuplicateDatabaseDialog({ source, onDuplicated, onClose }: Props) {
  const { t } = useTranslation();
  const duplicate = useDuplicateDatabase(source.id);
  const workspaces = useWorkspaces();
  const targets = (workspaces.data ?? []).filter((w) => w.permissions.includes(CREATE_DATABASE));
  const [name, setName] = useState(() =>
    t('databases.duplicate.defaultName', { name: source.name }).slice(0, DATABASE_NAME_MAX),
  );
  const [target, setTarget] = useState(source.workspace_id);
  const [records, setRecords] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [touched, setTouched] = useState(false);
  const cross = target !== source.workspace_id;
  const needsConfirmation = source.is_confidential && cross;
  const nameError = touched && name.trim() === '' ? t('validation.REQUIRED') : undefined;

  const submit = () => {
    setTouched(true);
    if (name.trim() === '' || (needsConfirmation && !confirmed)) return;
    duplicate.mutate(
      {
        name: name.trim(),
        ...(cross && { workspace_id: target }),
        include_records: records,
        ...(needsConfirmation && { confirm_cross_workspace: true }),
      },
      {
        onSuccess: ({ database, report }) => {
          onDuplicated(database, report);
        },
      },
    );
  };

  return (
    <Dialog title={t('databases.duplicate.title', { name: source.name })} onClose={onClose}>
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <TextField
          label={t('databases.duplicate.name')}
          value={name}
          maxLength={DATABASE_NAME_MAX}
          autoFocus
          error={nameError}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
        {targets.length > 1 && (
          <Combobox
            label={t('databases.duplicate.workspace')}
            value={target}
            options={targets.map((w) => ({
              value: w.id,
              label: w.workspace_type === 'personal' ? t('home.personalWorkspace') : w.name,
            }))}
            onChange={(next) => {
              setTarget(next);
              setConfirmed(false);
            }}
          />
        )}
        <fieldset className={styles.modes}>
          <legend>{t('databases.duplicate.mode')}</legend>
          <label className={styles.toggle}>
            <input
              type="radio"
              name="duplicate-mode"
              checked={!records}
              onChange={() => {
                setRecords(false);
              }}
            />
            <span>
              <strong>{t('databases.duplicate.structureOnly')}</strong>
              <span className={styles.hint}>{t('databases.duplicate.structureOnlyHint')}</span>
            </span>
          </label>
          <label className={styles.toggle}>
            <input
              type="radio"
              name="duplicate-mode"
              checked={records}
              onChange={() => {
                setRecords(true);
              }}
            />
            <span>
              <strong>{t('databases.duplicate.withRecords')}</strong>
              <span className={styles.hint}>{t('databases.duplicate.withRecordsHint')}</span>
            </span>
          </label>
        </fieldset>
        <p className={styles.notice} role="note">
          <Icons.lock /> {t('databases.duplicate.accessNote')}
          {source.is_confidential && ` ${t('databases.duplicate.confidentialNote')}`}
        </p>
        {records && (
          <p className={styles.warning} role="note">
            {t('databases.duplicate.personalData')}
            {cross && ` ${t('databases.duplicate.peopleDropped')}`}
          </p>
        )}
        {needsConfirmation && (
          <label className={styles.toggle}>
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(event) => {
                setConfirmed(event.target.checked);
              }}
            />
            <span>
              <strong>{t('databases.duplicate.confirmCross')}</strong>
              <span className={styles.hint}>{t('databases.duplicate.confirmCrossHint')}</span>
            </span>
          </label>
        )}
        <FormError error={duplicate.error} />
        <div className={styles.formActions}>
          <Button
            type="submit"
            variant="primary"
            disabled={duplicate.isPending || (needsConfirmation && !confirmed)}
          >
            {duplicate.isPending
              ? t('databases.duplicate.copying')
              : t('databases.duplicate.submit')}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
